import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Circle, Square, RefreshCw, Check, X, Loader2, SwitchCamera } from "lucide-react";

/**
 * VideoRecorder — inline webcam + mic recorder for student intro videos.
 *
 * Flow:
 *   1. Ask for camera + mic via navigator.mediaDevices.getUserMedia
 *   2. Show live preview
 *   3. On "Start recording": MediaRecorder captures at ~2Mbps VP9/Opus
 *      (falls back to WebM defaults if VP9 isn't supported)
 *   4. 5-minute hard cap — auto-stops at 300s
 *   5. On stop: replay the blob, offer "Retake" or "Use this"
 *   6. "Use this" → hands the Blob (wrapped as a File) up to onCapture
 *
 * All camera streams and blob URLs are released on unmount or retake so
 * the browser doesn't hold the camera light on longer than necessary.
 */
const MAX_SECONDS = 300; // 5 minutes

const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
};

const fmt = (s) => {
  const m = Math.floor(s / 60).toString().padStart(1, "0");
  const r = (s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
};

export const VideoRecorder = ({ onCapture, onCancel }) => {
  const liveRef = useRef(null);
  const playbackRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);

  const [status, setStatus] = useState("initialising"); // initialising | ready | recording | recorded | denied | unsupported
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [facing, setFacing] = useState("user");        // "user" (front) or "environment" (back)
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (liveRef.current) liveRef.current.srcObject = null;
  }, []);

  const requestCamera = useCallback(async (nextFacing) => {
    setError("");
    setStatus("initialising");
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia || !pickMimeType()) {
      setStatus("unsupported");
      return;
    }
    // Detect multiple video inputs so we can decide whether to show a flip button.
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === "videoinput");
      setHasMultipleCameras(cams.length > 1);
    } catch { /* ignore — flip button just stays hidden */ }

    // Release any previous stream before requesting the next facing mode
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    const wantFacing = nextFacing || facing;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: { ideal: wantFacing } },
        audio: true,
      });
      streamRef.current = stream;
      if (liveRef.current) {
        liveRef.current.srcObject = stream;
        liveRef.current.muted = true;
        await liveRef.current.play().catch(() => { /* autoplay policy — ok */ });
      }
      setStatus("ready");
    } catch (err) {
      const name = err?.name || "";
      setError(
        name === "NotAllowedError"
          ? "Camera and mic permission was blocked. Please allow access to record here, or upload a file instead."
          : name === "NotFoundError"
          ? "We couldn't find a camera. Please connect one or upload a file instead."
          : "Couldn't start the camera. Please try again or upload a file instead."
      );
      setStatus("denied");
    }
  }, [facing]);

  useEffect(() => {
    requestCamera();
    return () => {
      stopStream();
      if (tickRef.current) clearInterval(tickRef.current);
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    setSeconds(0);
    const mimeType = pickMimeType();
    try {
      const rec = new MediaRecorder(streamRef.current, mimeType ? { mimeType, videoBitsPerSecond: 2_000_000 } : undefined);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || "video/webm" });
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);
        setStatus("recorded");
        stopStream(); // release camera light
      };
      rec.start(1000); // 1s slice — makes the ondataavailable chunks land regularly
      setStatus("recording");
      tickRef.current = setInterval(() => {
        setSeconds((prev) => {
          const next = prev + 1;
          if (next >= MAX_SECONDS) {
            stopRecording();
          }
          return next;
        });
      }, 1000);
    } catch (e) {
      setError("Recording failed to start. Try a different browser or upload a file instead.");
    }
  };

  const stopRecording = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const retake = async () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setSeconds(0);
    await requestCamera(facing);
  };

  const useThis = () => {
    if (!recordedBlob) return;
    const ext = (recordedBlob.type.includes("mp4") ? "mp4" : "webm");
    const file = new File([recordedBlob], `intro-${Date.now()}.${ext}`, { type: recordedBlob.type });
    onCapture?.(file);
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
  };

  const flipCamera = async () => {
    // Only allowed while ready — flipping mid-record would truncate.
    if (status !== "ready") return;
    const next = facing === "user" ? "environment" : "user";
    setFacing(next);
    await requestCamera(next);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border-2 border-tangerine/40 bg-bone p-6"
      data-testid="video-recorder"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
          <Camera className="h-3.5 w-3.5" strokeWidth={2} />
          Record in browser
        </div>
        <button
          type="button"
          onClick={() => { stopStream(); onCancel?.(); }}
          data-testid="video-recorder-close"
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-olive hover:text-ink"
        >
          <X className="h-3.5 w-3.5" /> Close
        </button>
      </div>

      <div className="mt-4 overflow-hidden rounded-md bg-ink" style={{ aspectRatio: "16 / 9" }}>
        {status === "recorded" ? (
          <video
            ref={playbackRef}
            src={recordedUrl}
            controls
            playsInline
            className="h-full w-full object-cover"
            data-testid="video-recorder-playback"
          />
        ) : (
          <video
            ref={liveRef}
            autoPlay
            muted
            playsInline
            className={`h-full w-full object-cover ${status === "recording" ? "" : "opacity-90"}`}
            data-testid="video-recorder-preview"
          />
        )}
      </div>

      {status === "initialising" && (
        <div className="mt-4 flex items-center gap-2 text-[13px] text-olive">
          <Loader2 className="h-4 w-4 animate-spin" /> Starting your camera…
        </div>
      )}

      {(status === "denied" || status === "unsupported") && (
        <div className="mt-4 space-y-3">
          <p role="alert" data-testid="video-recorder-error" className="text-[13px] text-scarlet">
            {status === "unsupported"
              ? "In-browser recording isn't supported on this browser. Please upload a file instead."
              : error}
          </p>
          <button
            type="button"
            onClick={() => { stopStream(); onCancel?.(); }}
            className="rounded-full bg-ink px-4 py-2 text-base font-semibold text-sand hover:bg-forest"
          >
            Back to upload
          </button>
        </div>
      )}

      {status === "ready" && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[13px] text-olive">
            Ready when you are. Up to <strong className="text-ink">5 minutes</strong>. Look at the camera, keep it warm — donors want to meet you.
          </p>
          <div className="flex items-center gap-2">
            {hasMultipleCameras && (
              <button
                type="button"
                onClick={flipCamera}
                data-testid="video-recorder-flip"
                aria-label={facing === "user" ? "Switch to back camera" : "Switch to front camera"}
                title={facing === "user" ? "Switch to back camera" : "Switch to front camera"}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-sand px-3.5 py-2.5 text-[13px] font-semibold text-ink transition-colors hover:border-ink"
              >
                <SwitchCamera className="h-4 w-4" strokeWidth={1.9} />
                <span className="hidden sm:inline">
                  {facing === "user" ? "Back camera" : "Front camera"}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={startRecording}
              data-testid="video-recorder-start"
              className="inline-flex items-center gap-2 rounded-full bg-scarlet px-5 py-2.5 text-base font-semibold text-sand transition-colors hover:brightness-110"
            >
              <Circle className="h-4 w-4 fill-current" strokeWidth={0} /> Start recording
            </button>
          </div>
        </div>
      )}

      {status === "recording" && (
        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-scarlet/12 px-3 py-1 text-[13px] font-semibold text-scarlet">
              <span className="h-2 w-2 animate-pulse rounded-full bg-scarlet" /> REC
            </span>
            <span className="font-serif text-3xl text-ink tabular-nums" data-testid="video-recorder-timer">
              {fmt(seconds)}
              <span className="ml-1 text-[13px] text-olive">/ {fmt(MAX_SECONDS)}</span>
            </span>
          </div>
          <button
            type="button"
            onClick={stopRecording}
            data-testid="video-recorder-stop"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
          >
            <Square className="h-4 w-4 fill-current" strokeWidth={0} /> Stop
          </button>
        </div>
      )}

      {status === "recorded" && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[13px] text-olive">
            Take a look. Keep this one, or record another.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={retake}
              data-testid="video-recorder-retake"
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-sand px-4 py-2 text-base font-semibold text-ink hover:border-ink"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Retake
            </button>
            <button
              type="button"
              onClick={useThis}
              data-testid="video-recorder-use"
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2 text-base font-semibold text-sand hover:bg-forest"
            >
              <Check className="h-4 w-4" /> Use this
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};
